import { notification } from 'antd'
import axios from '../axiosInstance'
import constants from '../../constants'
import { handleErrors, definedErrorCodes } from '../../helpers'
import type {
  ExtensionApp,
  ExtensionListResponse,
  ExtensionListType,
  ExtensionStatusFallback,
  ExtensionStatusResponse,
} from './types'

/** The `installed` branch yields a double slash; kept for parity with the frozen `apps/*` saga. */
const listUrl = (app: ExtensionApp, type: ExtensionListType): string =>
  type === 'installed'
    ? `${constants.customerDomain}/apps/${app.id}/extensions?per_page=50`
    : `${constants.customerDomain}regions/${app.region}/extensions?per_page=50`

/** Region catalog, or the app's installed extensions when `type` is `'installed'`. Resolves the
 *  API's error body rather than rejecting, so callers branch on the shape instead of catching. */
export async function list(
  app: ExtensionApp,
  type: ExtensionListType = 'all',
): Promise<ExtensionListResponse> {
  return axios
    .get(listUrl(app, type))
    .then(res => res.data)
    .catch(error => {
      // Resolves `undefined` when the error carries no response (offline, DNS, CORS); every
      // caller already guards on `.data`, so they take their existing empty branch.
      const data = error?.response?.data
      let errorMsgs = handleErrors('')
      if (data?.error?.code !== undefined && definedErrorCodes(data.error.code)) {
        errorMsgs = handleErrors(data.error.code)
        notification.warning({
          message: errorMsgs.message,
          description: errorMsgs.description,
        })
      }
      return data
    })
}

/** Builds a malformed URL (second `?`) and is unreachable today; ported as-is, see docs. */
export async function search(
  app: ExtensionApp,
  searchText: string,
  type: ExtensionListType = 'all',
): Promise<ExtensionListResponse | unknown> {
  return axios
    .get(`${listUrl(app, type)}?searchKey=${searchText}`)
    .then(res => res.data)
    .catch(error => error)
}

/** Enable or disable an extension; `type` is the *current* state, so `'enabled'` turns it off.
 *  A refusal still returns HTTP 200 — always read the result through `isStatusSuccess`. */
export async function changeExtensionStatus(
  id: string,
  app: ExtensionApp,
  type: ExtensionListType = 'all',
): Promise<ExtensionStatusResponse | ExtensionStatusFallback> {
  const post = type === 'installed' || type === 'enabled' ? { disabled: [id] } : { enabled: [id] }

  return axios
    .post(`${constants.customerDomain}apps/${app.id}/extensions`, post)
    .then(res => res.data)
    .catch(error => {
      let errResponse: ExtensionStatusResponse | ExtensionStatusFallback = {
        data: 'Error occurred. Please contact CometChat Team if error persists.',
      }

      if (error.response) {
        errResponse = error.response.data
        if (
          error.response.data !== undefined &&
          error.response.data.error.code !== undefined &&
          definedErrorCodes(error.response.data.error.code)
        ) {
          const errorMsgs = handleErrors(error.response.data.error.code)
          notification.warning({
            message: errorMsgs.message,
            description: errorMsgs.description,
          })
        }
      }
      return errResponse
    })
}

/** The API answers HTTP 200 even when it refuses, putting the outcome in a per-id map. */
export function isStatusSuccess(
  response: ExtensionStatusResponse | ExtensionStatusFallback | undefined,
  id: string,
): boolean {
  const data = (response as ExtensionStatusResponse)?.data
  if (!data || typeof data === 'string') return false
  const entry = data.enabled?.[id] ?? data.disabled?.[id]
  return entry?.success === true
}

/** The per-id failure message, when there is one. */
export function statusMessage(
  response: ExtensionStatusResponse | ExtensionStatusFallback | undefined,
  id: string,
): string | undefined {
  const data = (response as ExtensionStatusResponse)?.data
  if (!data || typeof data === 'string') return undefined
  return (data.enabled?.[id] ?? data.disabled?.[id])?.message
}

/** Uninstall an extension. */
export async function removeExtension(
  id: string,
  app: ExtensionApp,
): Promise<ExtensionStatusResponse> {
  return axios
    .delete(`${constants.customerDomain}apps/${app.id}/extensions`, {
      data: { ids: [id] },
    })
    .then(res => res.data)
    .catch(error => {
      const data = error?.response?.data
      if (data?.error?.code !== undefined && definedErrorCodes(data.error.code)) {
        const errorMsgs = handleErrors(data.error.code)
        notification.warning({
          message: errorMsgs.message,
          description: errorMsgs.description,
        })
      }
      return data
    })
}
